import { IAiLimit } from 'services/repositories/AiLimitsRepository'
import HttpClient from 'services/HttpClient'
import {
  AddBrandPermissionRequest,
  BulkCreateRolePermissionsRequest,
  CreateRolePermissionRequest,
  CreateRoleRequest,
  CreateUserCountryPermissionRequest,
  CreateUserRequest,
  PaginatedResponse,
  PaginationQuery,
  SetDefaultCountryRequest,
  UpdateRoleRequest,
  UpdateUserRequest,
  UpdateUserRoleRequest} from 'types/userManagement'

export class UserManagementRepository {
  api: typeof HttpClient

  constructor(api: typeof HttpClient) {
    this.api = api
  }

  createUser(payload: CreateUserRequest) {
    return this.api.post('/users', payload)
  }

  getUsers(query?: PaginationQuery) {
    const params = new URLSearchParams()

    if (query?.limit) {
      params.append('limit[page]', query.limit.page.toString())
      params.append('limit[take]', query.limit.take.toString())
    }

    if (query?.order) {
      params.append('order[by]', query.order.by)
      params.append('order[dir]', query.order.dir)
    }

    if (query?.search) {
      params.append('search', String(query.search))
    }

    if (query?.filter) {
      Object.entries(query.filter).forEach(([key, value]) => {
        params.append(`filter[${key}]`, String(value))
      })
    }

    if (query?.filterByPart) {
      Object.entries(query.filterByPart).forEach(([key, value]) => {
        params.append(`filterByPart[${key}]`, String(value))
      })
    }

    const queryString = params.toString()

    return this.api.get(`/users${queryString ? `?${queryString}` : ''}`)
  }

  getAllUsers() {
    return this.api.get('/users/all')
  }

  getUserById(id: string) {
    return this.api.get(`/users/${id}`)
  }

  updateUser(id: string, payload: UpdateUserRequest) {
    return this.api.patch(`/users/${id}`, payload)
  }

  updateUserRole(id: string, payload: UpdateUserRoleRequest) {
    return this.api.patch(`/users/${id}/role`, payload)
  }

  deleteUser(id: string) {
    return this.api.delete(`/users/${id}`)
  }

  addBrandPermission(userId: string, brandId: string) {
    return this.api.post(`/users/${userId}/brands/${brandId}/permissions/add`)
  }

  removeBrandPermission(userId: string, brandId: string) {
    return this.api.post(
      `/brands/${brandId}/users/${userId}/permissions/remove`
    )
  }

  createRole(payload: CreateRoleRequest) {
    return this.api.post('/roles', payload)
  }

  getAllRoles() {
    return this.api.get('/roles/all')
  }

  getRoleById(id: string) {
    return this.api.get(`/roles/${id}`)
  }

  getRoleByName(name: string) {
    return this.api.get(`/roles/${name}/name`)
  }

  updateRole(id: string, payload: UpdateRoleRequest) {
    return this.api.patch(`/roles/${id}`, payload)
  }

  deleteRole(id: string) {
    return this.api.delete(`/roles/${id}`)
  }

  createRolePermission(payload: CreateRolePermissionRequest) {
    return this.api.post('/role-permissions', payload)
  }

  bulkCreateRolePermissions(payload: BulkCreateRolePermissionsRequest) {
    return this.api.post('/role-permissions/bulk', payload)
  }

  getAllPermissions() {
    return this.api.get('/role-permissions/permissions')
  }

  getAllRolePermissions() {
    return this.api.get('/role-permissions/all')
  }

  getRolePermissionById(id: string) {
    return this.api.get(`/role-permissions/${id}`)
  }

  deleteRolePermission(id: string) {
    return this.api.delete(`/role-permissions/${id}`)
  }

  createUserCountryPermission(payload: CreateUserCountryPermissionRequest) {
    return this.api.post('/user-country-permissions', payload)
  }

  getUserCountryPermissions(query?: PaginationQuery) {
    const params = new URLSearchParams()

    if (query?.limit) {
      params.append('limit[page]', query.limit.page.toString())
      params.append('limit[take]', query.limit.take.toString())
    }

    const queryString = params.toString()

    return this.api.get(
      `/user-country-permissions${queryString ? `?${queryString}` : ''}`
    )
  }

  getAllUserCountryPermissions() {
    return this.api.get('/user-country-permissions/all')
  }

  getUserCountryPermissionById(id: string) {
    return this.api.get(`/user-country-permissions/${id}`)
  }

  getUserCountryPermissionsByUser(userId: string) {
    return this.api.get(`/user-country-permissions/user/${userId}`)
  }

  setDefaultCountry(userId: string, payload: SetDefaultCountryRequest) {
    return this.api.post(
      `/user-country-permissions/user/${userId}/default-country/set`,
      payload
    )
  }

  deleteUserCountryPermission(id: string) {
    return this.api.delete(`/user-country-permissions/${id}`)
  }

  hardDeleteUserCountryPermission(id: string) {
    return this.api.delete(`/user-country-permissions/${id}/hard`)
  }

  getAllBrands() {
    return this.api.get('/brands/all')
  }

  /** All brands for permission management (e.g. master admin editing user brand permissions). */
  getAllBrandsForPermissionManagement() {
    return this.api.get('/brands/all-for-permission-management')
  }

  getBrandsList() {
    return this.api.get('/brands/list')
  }

  getBrands(query?: PaginationQuery) {
    const params = new URLSearchParams()

    if (query?.limit) {
      params.append('limit[page]', query.limit.page.toString())
      params.append('limit[take]', query.limit.take.toString())
    }

    if (query?.order) {
      params.append('order[by]', query.order.by)
      params.append('order[dir]', query.order.dir)
    }

    if (query?.search) {
      params.append('search', query.search)
    }

    const queryString = params.toString()

    return this.api.get(`/brands${queryString ? `?${queryString}` : ''}`)
  }

  getBrandById(id: string) {
    return this.api.get(`/brands/${id}`)
  }

  // AI Limits
  getAILimitStatus(params: {
    operationType: 'translation' | 'image_text_generation' | 'asset_image_text_generation'
    userId: string
    brandId?: string
    countryCode?: string
  }) {
    const searchParams = new URLSearchParams()

    searchParams.append('operationType', params.operationType)
    searchParams.append('userId', params.userId)
    if (params.brandId) searchParams.append('brandId', params.brandId)
    if (params.countryCode)
      searchParams.append('countryCode', params.countryCode)

    return this.api.get(`/ai-limits/status?${searchParams.toString()}`)
  }

  getApplicableAILimit(params: {
    operationType: 'translation' | 'image_text_generation' | 'asset_image_text_generation'
    brandId?: string
  }) {
    const searchParams = new URLSearchParams()

    searchParams.append('operationType', params.operationType)
    if (params.brandId) searchParams.append('brandId', params.brandId)

    return this.api.get(`/ai-limits/applicable?${searchParams.toString()}`)
  }

  createAILimit(payload: any) {
    return this.api.post('/ai-limits', payload)
  }

  createOneTimeAILimit(payload: any) {
    return this.api.post('/ai-limits/one-time', payload)
  }

  updateAILimit(limitId: string, payload: any) {
    return this.api.put(`/ai-limits/${limitId}`, payload)
  }

  resetAILimit(limitId: string) {
    return this.api.put(`/ai-limits/${limitId}/reset`)
  }

  getAllAILimits(params?: {
    userId?: string
    type?: 'user' | 'brand_country' | 'global'
    operationType?: 'translation' | 'image_text_generation' | 'asset_image_text_generation'
  }) {
    const queryParams = new URLSearchParams()
    
    if (params?.userId) {
      queryParams.append('userId', params.userId)
    }
    
    if (params?.type) {
      queryParams.append('type', params.type)
    }
    
    if (params?.operationType) {
      queryParams.append('operationType', params.operationType)
    }
    
    const queryString = queryParams.toString()

    return this.api.get<IAiLimit[]>(
      `/ai-limits${queryString ? `?${queryString}` : ''}`
    )
  }
}

export default new UserManagementRepository(HttpClient)
